<template>
  <v-card class="tw-max-w-[448px] tw-mx-auto !tw-rounded-xl">
    <div class="tw-flex tw-justify-between tw-px-6 tw-py-4 tw-items-center tw-w-full">
      <div class="tw-text-lg tw-font-semibold">{{ title }}</div>
      <v-btn
        class="tw-h-9 tw-w-2 !tw-bg-[#2E66FF] !tw-text-white hover:!tw-bg-[#F5D101]"
        variant="tonal"
        :disabled="isApplying"
        @click="onCancel"
      >
        X
      </v-btn>
    </div>

    <div class="tw-max-h-[70%] tw-overflow-auto">
      <div class="tw-px-6 tw-bg-[#fafafa] tw-mt-3">
        <p class="tw-text-base tw-font-light">
          {{ description }}
        </p>
        <div v-if="hasPrice" class="tw-flex tw-items-center tw-gap-1">
          <p class="tw-font-light">{{ priceChangeText }}</p>
          <span class="tw-text-orange-400 tw-font-bold"
            >${{ Number(toggleOptionData.configuredTotalMsrp).toLocaleString() }}</span
          >
        </div>
      </div>
      <div
        v-if="toggleOptionData.additionAffectedChromeOptionCodes?.length > 0"
        class="tw-px-6 tw-mt-3"
      >
        <p class="tw-font-bold tw-text-base tw-text-[#2E66FF]">{{ addingText }}</p>
        <div
          v-for="item in toggleOptionData.options"
          class="tw-border-gray-400 tw-p-3 tw-border"
          :key="item"
        >
          <div>
            <div class="tw-font-semibold tw-text-base">{{ item.optionKindName }}</div>
          </div>
          <div
            v-for="subItem in item.options"
            :key="subItem.chromeOptionCode"
            class="tw-flex tw-justify-between tw-items-center"
          >
            <p class="tw-font-light tw-text-base">{{ subItem.descriptions[0].description }}</p>
            <p class="tw-font-light tw-text-base">${{ subItem.msrp.toLocaleString() }}</p>
          </div>
        </div>
      </div>
      <div
        v-if="toggleOptionData.deletionAffectedChromeOptionCodes?.length > 0"
        class="tw-px-6 tw-mt-3"
      >
        <p class="tw-font-bold tw-text-base tw-text-orange-600">{{ removingText }}</p>
        <div
          v-for="item in toggleOptionData.options"
          class="tw-border-gray-400 tw-p-3 tw-border"
          :key="item"
        >
          <div>
            <div class="tw-font-semibold tw-text-base">{{ item.optionKindName }}</div>
          </div>
          <div
            v-for="subItem in item.options"
            :key="subItem.chromeOptionCode"
            class="tw-flex tw-justify-between tw-items-center"
          >
            <p class="tw-font-light tw-text-base">{{ subItem.descriptions[0].description }}</p>
            <p class="tw-font-light tw-text-base">${{ subItem.msrp.toLocaleString() }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="tw-flex tw-gap-3 tw-justify-end tw-py-4 tw-px-6">
      <v-btn
        class="tw-h-9 tw-w-[88px] !tw-bg-[#2E66FF] !tw-text-white hover:!tw-bg-[#F5D101]"
        variant="tonal"
        :disabled="isApplying"
        @click="onCancel"
      >
        {{ cancelText }}
      </v-btn>
      <v-btn
        class="tw-h-9 tw-w-[88px] !tw-bg-[#2E66FF] !tw-text-white hover:!tw-bg-[#F5D101]"
        variant="tonal"
        :loading="isApplying"
        :disabled="isApplying"
        @click="onApply"
      >
        {{ applyText }}
      </v-btn>
    </div>
  </v-card>
</template>

<script setup>
import { computed } from 'vue';
import { useCarsStore } from '@/stores/useCarsStore';
import { storeToRefs } from 'pinia';

defineProps({
  isApplying: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Change options?',
  },
  description: {
    type: String,
    default: 'Choosing this option will have the following effect.',
  },
  priceChangeText: {
    type: String,
    default: 'Change in total price is',
  },
  addingText: {
    type: String,
    default: 'Adding:',
  },
  removingText: {
    type: String,
    default: 'Removing:',
  },
  cancelText: {
    type: String,
    default: 'Cancel',
  },
  applyText: {
    type: String,
    default: 'Apply',
  },
});

const emit = defineEmits(['toggleDialog', 'apply', 'cancel']);
const carStore = useCarsStore();
const { toggleOptionData, modelConfigurationDetails } = storeToRefs(carStore);

const hasPrice = computed(
  () =>
    toggleOptionData.value?.configuredTotalMsrp != null &&
    !Number.isNaN(Number(toggleOptionData.value.configuredTotalMsrp)),
);

const applyDetails = () => {
  toggleOptionData.value?.options?.forEach((newOptionGroup) => {
    newOptionGroup?.options?.forEach((newOption) => {
      modelConfigurationDetails.value?.options?.forEach((existingOptionGroup) => {
        existingOptionGroup?.options?.forEach((existingOption) => {
          if (existingOption.chromeOptionCode === newOption.chromeOptionCode) {
            existingOption.selectionState = newOption.selectionState;
          }
        });
      });
    });
  });
};

const onApply = () => {
  applyDetails();
  emit('apply');
  emit('toggleDialog');
};

const onCancel = () => {
  emit('cancel');
  emit('toggleDialog');
};
</script>
